import { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';

// Utility to format date as YYYY-MM-DD
function formatDate(date) {
  return date.toISOString().slice(0, 10);
}

export default function useAnalyticsData() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [calendarData, setCalendarData] = useState([]); // For streak heatmap
  const [focusBarData, setFocusBarData] = useState([]); // For bar/line chart
  const [priorityBarData, setPriorityBarData] = useState([]); // For stacked bar
  const [donutData, setDonutData] = useState([]); // For estimate vs actual
  const [leaderboardData, setLeaderboardData] = useState([]); // For best day/week/month
  const [procrastinatedData, setProcrastinatedData] = useState([]); // For negative metric
  const [sessionDetails, setSessionDetails] = useState([]); // For session details table
  const [summaryData, setSummaryData] = useState({ daily: [], weekly: [], monthly: [] });

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    // Query sessions for last 30 days
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
  const startKey = formatDate(startDate); // eslint-disable-line no-unused-vars
    const sessionsRef = collection(db, 'sessions');
    const q = query(sessionsRef, where('userId', '==', user.uid));
    getDocs(q).then((snap) => {
      const sessions = snap.docs.map((d) => d.data()).sort((a,b) => new Date(b.at) - new Date(a.at));
      // Calendar data: streaks/focus minutes per day
      const calendarMap = {};
      sessions.forEach((s) => {
        const day = formatDate(new Date(s.at));
        calendarMap[day] = (calendarMap[day] || 0) + (s.mode === 'focus' ? Math.round(s.actualElapsed / 60) : 0);
      });
      const calendarArr = Object.entries(calendarMap).map(([day, value]) => ({ day, value }));
      setCalendarData(calendarArr);
      // Focus bar data: focus minutes and pomodoros per day
      const barMap = {};
      sessions.forEach((s) => {
        const day = formatDate(new Date(s.at));
        if (!barMap[day]) barMap[day] = { day, focus: 0, pomos: 0 };
        if (s.mode === 'focus') {
          barMap[day].focus += Math.round(s.actualElapsed / 60);
          barMap[day].pomos += 1;
        }
      });
      setFocusBarData(Object.values(barMap));
      // Procrastinated time (negative metric)
      const procrastinatedArr = sessions
        .filter((s) => typeof s.procrastinated === 'number')
        .map((s) => ({ name: formatDate(new Date(s.at)), value: Math.round(s.procrastinated) }));
      setProcrastinatedData(procrastinatedArr);
      // Session details: pauses, interruptions, attached todos, completed checklist
      const detailsArr = sessions.map((s) => ({
        at: s.at,
        mode: s.mode,
        rounds: s.rounds,
        actualElapsed: s.actualElapsed,
        procrastinated: s.procrastinated,
        pauses: s.pauses || [],
        interruptions: s.interruptions || [],
        attachedTodoIds: s.attachedTodoIds || [],
        completedChecklist: s.completedChecklist || [],
      }));
      setSessionDetails(detailsArr);
      // Daily/weekly/monthly summaries
      const dailyMap = {};
      const weeklyMap = {};
      const monthlyMap = {};
      sessions.forEach((s) => {
        const date = new Date(s.at);
        const day = formatDate(date);
        const week = `${date.getFullYear()}-W${Math.ceil((date.getDate() - 1 - date.getDay()) / 7) + 1}`;
        const month = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
        // Daily
        if (!dailyMap[day]) dailyMap[day] = { day, focus: 0, break: 0, todos: 0 };
        if (s.mode === 'focus') dailyMap[day].focus += Math.round(s.actualElapsed / 60);
        if (s.mode === 'break') dailyMap[day].break += Math.round(s.actualElapsed / 60);
        dailyMap[day].todos += (s.attachedTodoIds ? s.attachedTodoIds.length : 0);
        // Weekly
        if (!weeklyMap[week]) weeklyMap[week] = { week, focus: 0, break: 0, todos: 0 };
        if (s.mode === 'focus') weeklyMap[week].focus += Math.round(s.actualElapsed / 60);
        if (s.mode === 'break') weeklyMap[week].break += Math.round(s.actualElapsed / 60);
        weeklyMap[week].todos += (s.attachedTodoIds ? s.attachedTodoIds.length : 0);
        // Monthly
        if (!monthlyMap[month]) monthlyMap[month] = { month, focus: 0, break: 0, todos: 0 };
        if (s.mode === 'focus') monthlyMap[month].focus += Math.round(s.actualElapsed / 60);
        if (s.mode === 'break') monthlyMap[month].break += Math.round(s.actualElapsed / 60);
        monthlyMap[month].todos += (s.attachedTodoIds ? s.attachedTodoIds.length : 0);
      });
      setSummaryData({
        daily: Object.values(dailyMap),
        weekly: Object.values(weeklyMap),
        monthly: Object.values(monthlyMap),
      });
      // TODO: Add aggregation for priorityBarData, donutData, leaderboardData
      setLoading(false);
    });
  }, [user]);

  return {
    loading,
    calendarData,
    focusBarData,
    priorityBarData,
    donutData,
    leaderboardData,
    procrastinatedData,
    sessionDetails,
    summaryData,
  };
}
